import { ViewportRuler } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '@cbuild/core';
import { Subscription } from 'rxjs';
import { PermissionComponent } from '../../../permissions/permission-component';
import { LeftPanelTree, Node, SubNode } from './tree';

@Component({
  selector: 'cs-left-panel',
  standalone: false,
  templateUrl: './left-panel.component.html',
  styleUrls: ['./left-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LeftPanelComponent extends PermissionComponent
  implements OnInit, OnDestroy, OnChanges {
  @Input()
  public tree: LeftPanelTree = [];
  @Input()
  public title: string;
  @Input()
  public icon: string;
  // Customized checker for node or subnode
  @Input()
  public checker: (data: Node | SubNode) => boolean;

  @Input()
  @HostBinding('class.menu-collapsed')
  public set collapsed(val: boolean) {
    this._collapsed = val;
  }
  public get collapsed(): boolean {
    return this._collapsed;
  }

  @Input()
  public isLeadNurturingEnabled: boolean;

  @Output()
  public collapsedChange = new EventEmitter<boolean>();

  public containerHeight: number;

  private sub: Subscription;

  private _collapsed: boolean = false;

  constructor(
    authenticationService: AuthenticationService,
    private viewPortRuler: ViewportRuler,
    private router: Router,
    private route: ActivatedRoute
  ) {
    super(authenticationService);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.isLeadNurturingEnabled = changes.isLeadNurturingEnabled?.currentValue;
    const schedulerTree = this.tree.find(
      x => x.name === 'Scheduler Configuration'
    );
    if (schedulerTree) {
      if (this.isLeadNurturingEnabled) {
        schedulerTree.canShow = true;
      } else {
        schedulerTree.canShow = false;
      }
    }
  }

  public ngOnInit(): void {
    this.tree.forEach(_ => {
      const urlTree = this.router.createUrlTree(_.routerLink, {
        relativeTo: this.route
      });
      _.opened = this.router.isActive(urlTree, false);
      _.canShow = this.checkPermission(_);
      if (Array.isArray(_.children) && _.children.length > 0) {
        _.hasChildren = true;
        this.updateViewAccordingToPermissions(_);
      } else {
        _.hasChildren = false;
      }
    });
    this.adjustToHeight();
    this.sub = this.viewPortRuler.change().subscribe(_ => {
      this.adjustToHeight();
    });
  }

  private updateViewAccordingToPermissions(node: Node): void {
    node.children!.forEach(_ => {
      _.canShow = this.checkPermission(_);
    });
    // Updates canShow of parent when no children can be shown
    node.canShow = node.children!.some(_ => _.canShow);
  }

  public adjustToHeight(): void {
    const { height } = this.viewPortRuler.getViewportRect();
    this.containerHeight = height - 62;
  }

  public open(i: number): void {
    this.tree.forEach((node, index) => {
      node.opened = index === i ? !node.opened : false;
    });
  }

  public ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  public subRouteClick(event: Event): void {
    event.stopPropagation();
  }

  public toggleMenu(): void {
    this._collapsed = !this._collapsed;
    this.collapsedChange.emit(this._collapsed);
  }

  public closeAllNodes(): void {
    this.tree.forEach(node => {
      node.opened = false;
    });
  }

  private checkPermission(data: Node | SubNode): boolean {
    if (!data.permission) {
      // No need of permission check in node or subnode
      return true;
    }
    if (data.name === 'Scheduler Configuration') {
      return false;
    }
    return this.checker
      ? this.checker(data)
      : this.hasUserPermission(data.permission!, data.permissionCode!);
  }
}
